import { isOneTimeClass } from '@/helpers/class';
import type { FactTile } from '@/lib/types';
import { formatDays, formatHourCount } from '@/lib/utils';
import type { Class } from '@/state/class-groups';

// One rule per class page: with several instructors, the strictest of their
// schedules, so the page never promises more than any of them allows. No
// value means no rule.
const strictest = (
  values: (number | null | undefined)[],
  pick: (...values: number[]) => number
): number | null => {
  const set = values.filter((value): value is number => value != null);
  return set.length > 0 ? pick(...set) : null;
};

const formatRuleHours = (
  hours: number,
  formatHours = (hours: number) => `${hours}h`
): string =>
  hours >= 48 && hours % 24 === 0 ? formatDays(hours / 24) : formatHours(hours);

// The rules the class read serves already resolved per schedule.
export const getServedBookingRules = (classData: Class) => {
  const served = (classData.instructor_schedules ?? []).map(
    (schedule) => schedule.booking_rules
  );
  return {
    modificationNoticeHours: strictest(
      served.map((rules) => rules?.modification_notice_hours),
      Math.max
    ),
    minNoticeHours: strictest(
      served.map((rules) => rules?.min_notice_hours),
      Math.max
    ),
    maxDaysInAdvance: strictest(
      served.map((rules) => rules?.max_days_in_advance),
      Math.min
    ),
  };
};

// Without a min notice the page cannot name the rule that
// closed booking, so the line stays general. Whole days read as days, as the
// rules tile beside it does.
export const describeBookingClose = (minNoticeHours: number | null): string =>
  minNoticeHours
    ? `Bookings close ${formatRuleHours(
        minNoticeHours,
        formatHourCount
      )} before the class starts.`
    : 'The studio is no longer taking bookings for this class.';

export const getClassRules = (classData: Class): FactTile[] => {
  const {
    modificationNoticeHours: modification,
    minNoticeHours: minNotice,
    maxDaysInAdvance: maxDays,
  } = getServedBookingRules(classData);

  const rules: (FactTile | false)[] = [
    // With no notice set, the backend lets a customer cancel or reschedule
    // until the class starts. A one-time class has no other date to move to.
    {
      key: 'modification',
      illustration: 'clock-3d',
      lead: isOneTimeClass(classData) ? 'Cancel ' : 'Cancel or reschedule ',
      ...(modification
        ? {
            prefix: 'up to ',
            value: formatRuleHours(modification),
            tail: ' before',
          }
        : { value: 'anytime', tail: ' before class' }),
    },
    !!minNotice && {
      key: 'min_notice',
      illustration: 'waitlist-hourglass-3d',
      lead: 'Book at least ',
      value: formatRuleHours(minNotice),
      tail: ' before class',
    },
    // 0 days ahead is a rule of its own: the backend sells only the same day.
    maxDays !== null && {
      key: 'max_days',
      illustration: 'calendar-blank-3d',
      ...(maxDays === 0
        ? { lead: 'Book on the ', value: 'day of class', tail: ' only' }
        : { lead: 'Book up to ', value: formatDays(maxDays), tail: ' ahead' }),
    },
  ];

  return rules.filter((rule): rule is FactTile => !!rule);
};
